import { useQuery } from 'react-query'
import api from 'balkerne-core/api'

export type HurricaneForecast = {
  name: string
  basin_id: string
  year: number
  gov_id: string
  is_active: boolean
  is_subtropical: boolean
  forecasts: {
    latitude: number
    longitude: number
    date: string
    sustained_wind: number
    max_gust_wind: number
    status: string
    max_radius: number
  }[]
  history: {
    latitude: number
    longitude: number
    date: string
    sustained_wind: number
    max_gust_wind: number
    status: string
    movement_degrees: number
    movement_speed: number
  }[]
}

export const useHurricaneForecasts = () => {
  const path = '/analytics/hurricanes/forecasts'
  const fetch = async () => await api.get(path).then(res => res.data)
  const { data, ...rest } = useQuery<HurricaneForecast[]>([path], fetch, {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })
  return { ...rest, data: data ?? [] }
}

export type HurricaneHistorical = {
  sid: string
  name: string
  last_timestamp: string
  year: number
}

export const useHurricanes = () => {
  const path = '/analytics/hurricanes'
  const fetch = async () => await api.get(path).then(res => res.data)
  const { data, ...rest } = useQuery<HurricaneHistorical[]>([path], fetch, {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })
  return { ...rest, data: data ?? [] }
}

export type HurricaneHistoricalTrack = {
  lat: number
  lon: number
  direction: number
  velocity: number
  track_type: string
  nature: string
  date: string
  agency: string
  interpolated: boolean
  roci: number | null
  wind_speed: number | null
  pressure: number | null
}

export const useHurricaneTrack = (sid: string | null) => {
  const path = `/analytics/hurricanes/${sid}`
  const fetch = async () => await api.get(path).then(res => res.data)
  const { data, ...rest } = useQuery<HurricaneHistoricalTrack[]>([path], fetch, {
    enabled: sid !== null,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })
  return { ...rest, data: data ?? [] }
}

export const useThresholdRecommendation = (stationId, config = {}) => {
  const path = `/analytics/get_typical_high/${stationId}`
  const fetch = async () => await api.get(path).then(res => res.data)
  return useQuery([path], fetch, {
    ...config,
  })
}

export const useClimateHazards = () => {
  const path = '/analytics/cc/hazards'
  const fetcher = async () => (await api.get(path)).data
  const { data, ...rest } = useQuery(path, fetcher, {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })
  return { ...rest, data: data ?? [] }
}

type FacetType = 'enum' | 'text' | 'bool'

export interface FacetDefinition {
  type: FacetType
  values?: string[]
}

export interface Metric {
  id: number
  hazard: string
  code: string
  label: string
  unit: string
  value_type: string
  facet_schema: Record<string, FacetDefinition>
  facet_defaults: Record<string, unknown>
  tags: Record<string, unknown>
}

export async function getClimateMetrics(hazard?: string): Promise<Metric[]> {
  const res = await api.get('/analytics/cc/metrics', {
    params: hazard ? { hazard } : undefined,
  })
  return res.data as Metric[]
}

export const useClimateMetrics = (hazard?: string) => {
  return useQuery({
    queryKey: ['climate-metrics', hazard ?? 'all'],
    queryFn: () => getClimateMetrics(hazard),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })
}
